/**
 * Which conversations are mid-turn right now, so the rail can show a live dot.
 *
 * This is push-derived UI state (driven by `turn.started` / `turn.completed`
 * from the conversation WebSocket), not server state — it never comes back from
 * a request, so it lives in a tiny module-level store rather than React Query.
 */
import { useSyncExternalStore } from 'react';

const liveConversationIds = new Set<string>();
const listeners = new Set<() => void>();
let snapshot: ReadonlySet<string> = new Set<string>();

function emit(): void {
  snapshot = new Set(liveConversationIds);
  for (const listener of listeners) listener();
}

export function markTurnStarted(conversationId: string): void {
  if (liveConversationIds.has(conversationId)) return;
  liveConversationIds.add(conversationId);
  emit();
}

export function markTurnCompleted(conversationId: string): void {
  if (!liveConversationIds.delete(conversationId)) return;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ReadonlySet<string> {
  return snapshot;
}

/** Conversation ids with an in-flight agent turn. */
export function useLiveConversationIds(): ReadonlySet<string> {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
